import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { fuseAnimations } from '@fuse/animations';
import { sentry } from 'sentry';

import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { Equipment, EquipmentAttachment } from '@core/interfaces';
import { UploadService } from '@core/services/upload.service';
import { fileToBase64 } from '@core/utils/file-converter';
import { TranslateService } from '@ngx-translate/core';
import { EquipmentsService } from 'app/main/equipments/services/equipments-resolver.service';

@Component({
  selector: 'tab-attachments',
  templateUrl: './tab-attachments.component.html',
  styleUrls: ['./tab-attachments.component.scss'],
  animations: fuseAnimations
})
export class TabAttachmentsComponent implements OnInit {
  @Input() equipment: Equipment;
  @Input() isViewMode: boolean;

  attachmentsFormArray: FormArray;

  images = {
    isUploading: false,
    hasErrors: {
      type: false,
      size: false,
      max: false
    }
  };

  isSaving = false;
  updatingAttachmentId = 0;

  constructor(
    private fb: FormBuilder,
    private imageUploadService: UploadService,
    private translateService: TranslateService,
    private equipmentsService: EquipmentsService,
    private customMessageService: CustomMessagesService
  ) { }

  ngOnInit(): void {
    const attachments = this.equipment.attachments.filter((attachment) => !attachment.archived);
    this.setAttachments(attachments);
  }

  setAttachments(attachments: EquipmentAttachment[]): void {
    this.attachmentsFormArray = this.fb.array(attachments.map((attachment) => {
      return this.fb.group({
        id: this.fb.control(attachment.id),
        title: this.fb.control(attachment.title),
        newTitle: this.fb.control(attachment.title),
        path: this.fb.control(attachment.path),
        size: this.fb.control(attachment.size),
        createdAt: this.fb.control(attachment.createdAt),
        isChangingTitle: this.fb.control(false),
        isDeleting: this.fb.control(false)
      });
    }));
  }

  enableChangeAttachmentTitle(attachment: FormGroup, attachmentIndex: number): void {
    attachment.get('isChangingTitle').setValue(true);

    setTimeout(() => {
      document.getElementById(`attachment-${attachmentIndex}-title`).focus();
    } , 0);
  }

  cancelChangeAttachmentTitle(attachment: FormGroup): void {
    attachment.get('isChangingTitle').setValue(false);

    if (attachment.value.title !== attachment.value.newTitle) {
      attachment.get('newTitle').setValue(null);
    }
  }

  imageValidation(imagesInput): boolean {
    const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

    this.images.hasErrors.type = false;
    this.images.hasErrors.type = false;
    this.images.hasErrors.size = false;
    this.images.hasErrors.max = false;

    if (!imagesInput.length) {
      return false;
    }

    if (imagesInput.length > 20) {
      this.images.hasErrors.max = true;
      return false;
    }

    const hasAllImagesWithAcceptableType = imagesInput.every((imageInput) => {
      return acceptedTypes.includes(imageInput.type);
    });

    const hasAllImagesWithAcceptableSize = imagesInput.every((imageInput) => {
      return imageInput.size <= 2097152;
    });


    if (!hasAllImagesWithAcceptableType) {
      this.images.hasErrors.type = true;
      return false;
    }

    if (!hasAllImagesWithAcceptableSize) {
      this.images.hasErrors.size = true;
      return false;
    }

    return true;
  }

  async addNewAttachments(event: any): Promise<void> {
    const imagesInput = event.target.files;
    const imagesArray: any[] = Array.from(imagesInput);

    const imagesValidation = this.imageValidation(imagesArray);
    if (!imagesValidation) {
      return;
    }

    this.images.isUploading = true;

    try {
      for (let imageIndex = 0; imageIndex < imagesArray.length; imageIndex++) {
        const imageInformation = {
          imageBase64Encoded: await fileToBase64(imagesInput[imageIndex]),
          filename: imagesArray[imageIndex].name,
          size: imagesArray[imageIndex].size,
          path: `equipments/${this.equipment.id}/attachments`
        };

        const imageUploaded = await this.imageUploadService.upload(imageInformation);
        const attachmentAdded = await this.equipmentsService.saveEquipmentAttachment(null, {
          path: imageUploaded.path,
          size: imageUploaded.size,
          equipmentId: this.equipment.id
        });

        const newAttachmentIndex = this.attachmentsFormArray.controls.length;
        const newAttachmentControl = this.fb.group({
          id: this.fb.control(attachmentAdded.id),
          title: this.fb.control(attachmentAdded.title),
          newTitle: this.fb.control(attachmentAdded.title),
          path: this.fb.control(attachmentAdded.path),
          size: this.fb.control(attachmentAdded.size),
          createdAt: this.fb.control(attachmentAdded.createdAt),
          isChangingTitle: this.fb.control(false),
          isDeleting: this.fb.control(false)
        });

        this.attachmentsFormArray.setControl(newAttachmentIndex, newAttachmentControl);
      }

      const successMessage = imagesArray.length > 1 ? 'ALL_SUCCESS' : 'SUCCESS';
      this.customMessageService.success(`EQUIPMENTS.DIALOG.ATTACHMENTS.MESSAGES.${successMessage}`);
    } catch (err) {
      const errorMessage = imagesArray.length > 1 ? 'ALL_ERROR' : 'ERROR';
      this.customMessageService.error(`EQUIPMENTS.DIALOG.ATTACHMENTS.MESSAGES.${errorMessage}`);

      sentry.captureException(err);
    } finally {
      this.images.isUploading = false;
    }
  }

  async updateAttachmentTitle(attachment: FormGroup): Promise<void> {
    attachment.get('isChangingTitle').setValue(false);
    this.updatingAttachmentId = attachment.value.id;
    this.isSaving = true;

    const input = {
      title: attachment.value.newTitle,
      path: attachment.value.path,
      size: attachment.value.size,
      equipmentId: this.equipment.id
    };

    try {
      await this.equipmentsService.saveEquipmentAttachment(attachment.value.id, input);

      attachment.get('title').setValue(input.title);
      this.customMessageService.success('EQUIPMENTS.DIALOG.ATTACHMENTS.MESSAGES.SUCCESS');
    } catch (err) {
      this.customMessageService.error('EQUIPMENTS.DIALOG.ATTACHMENTS.MESSAGES.ERROR');
      sentry.captureException(err);
    } finally {
      this.isSaving = false;
      this.updatingAttachmentId = 0;
    }
  }

  async deleteAttachment(attachment: FormGroup, attachmentIndex: number): Promise<void> {
    this.isSaving = true;

    try {
      await this.equipmentsService.saveEquipmentAttachment(attachment.value.id, null);

      this.attachmentsFormArray.removeAt(attachmentIndex);
      this.customMessageService.success('EQUIPMENTS.DIALOG.ATTACHMENTS.MESSAGES.SUCCESS_DELETE');
    } catch (err) {
      this.customMessageService.error('EQUIPMENTS.DIALOG.ATTACHMENTS.MESSAGES.ERROR_DELETE');
      sentry.captureException(err);
    } finally {
      this.isSaving = false;
    }
  }
}
